import { useState, useEffect } from "react";
import { TrendingUp, Loader2, Library, ArrowUpRight } from "lucide-react";
import { PokemonCardTile } from "./PokemonCardTile";
import { makeVariant } from "../lib/variants";
import { eur } from "../lib/prices";
import { fetchCollectionValue } from "../lib/api";

// Vue « Valeur estimée » : la collection classée du plus cher au moins cher,
// plus les cartes qui ont le plus monté récemment.
//
// ⚠️ La hausse est mesurée « tendance du jour vs moyenne 30 jours », deux
// champs que TCGdex fournit pour CHAQUE carte. L'historique maison
// (`price_history`) ne couvre que les fiches déjà ouvertes : il ne dirait
// presque rien ici.
export const CollectionValueView = ({
  collection, conditions = {}, onStatusChange, onRemove, onCardClick,
}) => {
  const [data, setData] = useState({ cards: [], total: 0, risers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCollectionValue()
      .then(setData)
      .catch(err => console.error("Valeur de la collection :", err))
      .finally(() => setLoading(false));
  }, []);

  const handleVariantClick = (cardId, variantType, currentStatus, shiftKey) => {
    if (shiftKey) {
      if (currentStatus === "wanted") onRemove(cardId, variantType);
      else onStatusChange(cardId, variantType, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") onStatusChange(cardId, variantType, "owned");
      else onRemove(cardId, variantType);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-red-500" /></div>;
  }

  const priced = data.cards.filter(c => c.variant_price);
  const unpriced = data.cards.filter(c => !c.variant_price);

  const tile = (card) => (
    <PokemonCardTile
      key={`${card.id}::${card.owned_variant}`}
      card={card}
      collectionMap={collection}
      conditionMap={conditions}
      onCardClick={onCardClick}
      onVariantClick={handleVariantClick}
      cardVariants={[makeVariant(card.owned_variant)]}
    />
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-red-400" />Valeur estimée
      </h2>
      <p className="text-sm text-zinc-500 mb-6">
        Prix tendance Cardmarket servis par TCGdex, mis à jour environ une fois par jour.
        C'est une estimation, pas une cote de vente.
      </p>

      <div className="mb-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 flex flex-wrap gap-8">
        <div>
          <div className="text-sm uppercase tracking-wider text-zinc-500 mb-1">Total estimé</div>
          <div className="text-4xl font-bold text-white">{eur(data.total) || "—"}</div>
        </div>
        <div>
          <div className="text-sm uppercase tracking-wider text-zinc-500 mb-1">Cartes cotées</div>
          <div className="text-4xl font-bold text-white">{priced.length}</div>
          <div className="text-xs text-zinc-500 mt-1">sur {data.cards.length} possédées</div>
        </div>
      </div>

      {data.risers.length > 0 && (
        <div className="mb-10">
          <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-1">
            <ArrowUpRight className="w-5 h-5 text-emerald-400" />En hausse récente
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            Cartes dont la tendance du jour dépasse leur moyenne des 30 derniers jours.
          </p>
          <div className="card-grid">
            {data.risers.map(card => (
              <div key={`riser-${card.id}::${card.owned_variant}`}>
                {tile(card)}
                <div className="mt-1 px-2 text-xs text-emerald-400 font-semibold">
                  +{card.delta_pct}% <span className="text-zinc-500 font-normal">
                    ({eur(card.avg30)} → {eur(card.variant_price)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-lg font-bold text-white mb-4">Les plus chères</h3>
      {data.cards.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Library className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune carte possédée pour l'instant.</p>
        </div>
      ) : (
        <div className="card-grid">{priced.map(tile)}</div>
      )}

      {/* Les cartes sans cote sont mises à part : les laisser dans le
          classement à 0 € donnait l'impression qu'elles ne valent rien, alors
          que Cardmarket n'a simplement pas de prix pour elles (promos, énergies,
          vieux sets). */}
      {unpriced.length > 0 && (
        <div className="mt-10">
          <h3 className="text-lg font-bold text-white mb-1">Sans cote Cardmarket</h3>
          <p className="text-sm text-zinc-500 mb-4">
            {unpriced.length} carte{unpriced.length > 1 ? "s" : ""} pour lesquelles TCGdex
            ne fournit aucun prix — souvent des promos, des énergies ou des extensions
            anciennes. Ce n'est pas une valeur nulle, c'est une absence de donnée.
          </p>
          <div className="card-grid">{unpriced.map(tile)}</div>
        </div>
      )}
    </div>
  );
};
